import { useEffect, useState } from "react";
import type { CreateTaskInput, Task, TaskPriority, TaskStatus } from "../types";

interface TaskFormProps {
	task?: Task;
	onSubmit: (data: CreateTaskInput) => Promise<void>;
	onCancel: () => void;
	isSubmitting?: boolean;
}

export function TaskForm({ task, onSubmit, onCancel, isSubmitting = false }: TaskFormProps) {
	const [title, setTitle] = useState(task?.title || "");
	const [description, setDescription] = useState(task?.description || "");
	const [status, setStatus] = useState<TaskStatus>(task?.status || "todo");
	const [priority, setPriority] = useState<TaskPriority>(task?.priority || "medium");
	const [dueDate, setDueDate] = useState(task?.dueDate?.split("T")[0] || "");
	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (task) {
			setTitle(task.title);
			setDescription(task.description);
			setStatus(task.status);
			setPriority(task.priority);
			setDueDate(task.dueDate?.split("T")[0] || "");
		}
	}, [task]);

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!title.trim()) {
			newErrors.title = "タイトルは必須です";
		} else if (title.length > 100) {
			newErrors.title = "タイトルは100文字以内で入力してください";
		}

		if (description.length > 1000) {
			newErrors.description = "説明は1000文字以内で入力してください";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validate()) return;

		const data: CreateTaskInput = {
			title: title.trim(),
			description: description.trim(),
			status,
			priority,
			dueDate: dueDate || null,
		};

		await onSubmit(data);
	};

	const isEditing = !!task;

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<div>
				<label htmlFor="title" className="label">
					タイトル <span className="text-red-500">*</span>
				</label>
				<input
					type="text"
					id="title"
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					className={`input ${errors.title ? "border-red-500 focus:ring-red-500" : ""}`}
					placeholder="タスクのタイトルを入力"
					disabled={isSubmitting}
				/>
				{errors.title && (
					<p className="mt-1 text-sm text-red-600" role="alert">
						{errors.title}
					</p>
				)}
			</div>

			<div>
				<label htmlFor="description" className="label">
					説明
				</label>
				<textarea
					id="description"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					rows={4}
					className={`input resize-none ${errors.description ? "border-red-500 focus:ring-red-500" : ""}`}
					placeholder="タスクの詳細を入力（任意）"
					disabled={isSubmitting}
				/>
				{errors.description && (
					<p className="mt-1 text-sm text-red-600" role="alert">
						{errors.description}
					</p>
				)}
			</div>

			<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
				<div>
					<label htmlFor="status" className="label">
						ステータス
					</label>
					<select
						id="status"
						value={status}
						onChange={(e) => setStatus(e.target.value as TaskStatus)}
						className="input"
						disabled={isSubmitting}
					>
						<option value="todo">未着手</option>
						<option value="in_progress">進行中</option>
						<option value="done">完了</option>
					</select>
				</div>

				<div>
					<label htmlFor="priority" className="label">
						優先度
					</label>
					<select
						id="priority"
						value={priority}
						onChange={(e) => setPriority(e.target.value as TaskPriority)}
						className="input"
						disabled={isSubmitting}
					>
						<option value="low">低</option>
						<option value="medium">中</option>
						<option value="high">高</option>
					</select>
				</div>

				<div>
					<label htmlFor="dueDate" className="label">
						期限
					</label>
					<input
						type="date"
						id="dueDate"
						value={dueDate}
						onChange={(e) => setDueDate(e.target.value)}
						className="input"
						disabled={isSubmitting}
					/>
				</div>
			</div>

			<div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
				<button
					type="button"
					onClick={onCancel}
					className="btn-secondary w-full sm:w-auto"
					disabled={isSubmitting}
				>
					キャンセル
				</button>
				<button type="submit" className="btn-primary w-full sm:w-auto" disabled={isSubmitting}>
					{isSubmitting ? (
						<span className="flex items-center gap-2">
							<svg
								className="animate-spin h-4 w-4"
								fill="none"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<circle
									className="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									strokeWidth="4"
								/>
								<path
									className="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								/>
							</svg>
							保存中...
						</span>
					) : isEditing ? (
						"更新する"
					) : (
						"作成する"
					)}
				</button>
			</div>
		</form>
	);
}
